import Link from 'next/link'

import { Shimmer } from '@wartek-id/shimmer'
import { Button } from '@wartek-id/button'
import { Icon } from '@wartek-id/icon'

import InfoBox from 'app/PelatihanMandiriPage/partials/Topic/InfoBox'
import { useSETrackerEvent } from 'app/PelatihanMandiriPage/hooks/useSETrackEvent'

import type { OverallProgress } from 'types/microlearning'

type TopicProgressProps = {
  isLoading: boolean
  selectedProgress?: OverallProgress
  isError: boolean
}
const LoadingComponent = (
  <div>
    <Shimmer className="rounded-md mb-2" />
    <div className="py-4 px-4 bg-white rounded-md border border-gray-20 relative h-28">
      <div className="flex justify-between">
        <div>
          <Shimmer width={120} className="rounded-md mb-2" />
          <Shimmer className="rounded-md" />
        </div>
        <Shimmer className="rounded-md" height={64} width={64} />
      </div>
    </div>
  </div>
)

const TopicProgress = ({
  isLoading,
  selectedProgress,
  isError,
}: TopicProgressProps) => {
  const { SETrackProgressTopic } = useSETrackerEvent()

  if (isLoading) {
    return LoadingComponent
  }

  // sorted modules by id
  selectedProgress?.modules?.sort((a, b) => b.moduleId - a.moduleId)

  const status =
    selectedProgress?.percentage !== 100
      ? 'IN_PROGRESS'
      : selectedProgress?.aksiNyata?.state
  const title = status === 'IN_PROGRESS' ? 'MODUL' : 'TOPIK'

  const notLearnedModule =
    selectedProgress?.modules?.find(
      (module) =>
        module.postTest === false && module.prerequisiteCompleted === true
    ) || selectedProgress?.modules[0]

  const modulCount = {
    total: selectedProgress?.modules?.length,
    learned: selectedProgress?.modules?.filter((module) => module.postTest)
      ?.length,
  }

  const content = {
    image:
      status === 'IN_PROGRESS'
        ? notLearnedModule?.imagePath
        : selectedProgress?.imageUrl,
    title:
      status === 'IN_PROGRESS'
        ? notLearnedModule?.name
        : selectedProgress?.name,
  }

  const URL_CONTINUE = {
    NOT_CREATED: `/pelatihan-mandiri/topik/${selectedProgress?.id}/aksi-nyata?topik_name=${selectedProgress?.name}`,
    CREATED: `/pelatihan-mandiri/topik/${selectedProgress?.id}/aksi-nyata?topik_name=${selectedProgress?.name}`,
    CONFIRMED: `/pelatihan-mandiri/topik/${selectedProgress?.id}/aksi-nyata/form`,
    NEED_REVISION: `/pelatihan-mandiri/topik/${selectedProgress?.id}/aksi-nyata/form`,
    IN_PROGRESS: `/pelatihan-mandiri/modul/${notLearnedModule?.moduleId}`,
  }

  const URL_TOPIC = `/pelatihan-mandiri/topik/${selectedProgress?.id}`

  const CIRCLE_ICON = {
    NOT_CREATED: {
      name: 'batch_prediction',
      variant: 'inverse',
      color: '#103178',
    },
    CREATED: {
      name: 'batch_prediction',
      variant: 'inverse',
      color: '#103178',
    },
    CONFIRMED: {
      name: 'batch_prediction',
      variant: 'inverse',
      color: '#103178',
    },
    IN_PROGRESS: {
      name: 'lock',
      variant: 'disabled',
      color: '#ffffff',
    },
    NEED_REVISION: {
      name: 'border_color',
      variant: 'inverse',
      color: '#FFC453',
    },
  }

  const BUTTON_TEXT = {
    NOT_CREATED: 'Lanjut Aksi Nyata',
    CREATED: 'Lanjut Aksi Nyata',
    CONFIRMED: 'Lanjut Aksi Nyata ',
    IN_PROGRESS: 'Lanjut pelajari ',
    NEED_REVISION: 'Perbaiki Aksi Nyata',
  }

  const handleTrackerOpenTopic = () => {
    SETrackProgressTopic({
      action_type: 'click_progress_topic',
      action_value: 'buka_topik',
      destination_url: URL_TOPIC,
    })
  }

  const handleTrackerToActivity = () => {
    SETrackProgressTopic({
      action_type: 'click_progress_topic',
      action_value: 'to_activity',
      destination_url: URL_CONTINUE[status],
    })
  }

  return (
    <div>
      <div className="flex items-start mb-2">
        <div className="flex items-center">
          <img
            src="https://storage.googleapis.com/microlearning-public-production/web-assets/progress-topic-icon.png"
            height={24}
            width={24}
            alt="Topik Progress"
          />
        </div>
        <div className="flex-1 ml-2">
          <div className="font-semibold text-lg">
            Progres topik Anda sebelumnya
          </div>
        </div>
      </div>
      {!!selectedProgress && (
        <div className="bg-white rounded-md border border-gray-20 relative">
          <div className="p-4">
            <div className="flex justify-between">
              <div>
                <div className="text-md font-semibold text-gray-60 text-sm">
                  {title}
                </div>
                <div className="font-semibold flex-1">{content.title}</div>
              </div>
              {content.image && (
                <div className="flex-none ml-2">
                  <img
                    src={content.image}
                    className="rounded-md h-[64px] w-[64px] object-cover"
                    alt={content.title}
                  />
                </div>
              )}
            </div>

            {/* progress bar */}
            <div className="relative h-[6px] w-full rounded-md bg-gray-40 mt-4">
              {status !== 'IN_PROGRESS' && (
                <div
                  className={
                    'absolute h-[21px] w-[21px] rounded-full top-0 bottom-0 m-auto left-0 border-2 border-white flex items-center justify-center z-10 bg-green-50'
                  }
                >
                  <Icon
                    style={{ fontSize: '12px' }}
                    color={CIRCLE_ICON[status]?.variant as any}
                  >
                    check
                  </Icon>
                </div>
              )}

              {/* progress bar module */}
              <div
                className="absolute h-full left-0 bg-green-50 rounded-md"
                style={{ width: `${selectedProgress?.percentage / 1.5}%` }}
              />

              {/* progress bar module if need revision */}
              {status === 'NEED_REVISION' && (
                <div
                  className="absolute h-full right-0 bg-yellow-300 rounded-md"
                  style={{
                    width: `calc(50% / 1.5)`,
                  }}
                />
              )}

              <div
                className={
                  'absolute h-[21px] w-[21px] rounded-full top-0 bottom-0 m-auto right-0 border-2 border-white flex items-center justify-center'
                }
                style={{
                  backgroundColor: CIRCLE_ICON[status]?.color,
                  left: `36%`,
                }}
              >
                <Icon
                  style={{ fontSize: '10px' }}
                  color={CIRCLE_ICON[status]?.variant as any}
                >
                  {CIRCLE_ICON[status]?.name}
                </Icon>
              </div>
            </div>
            <div className="flex justify-between mt-3">
              <div className="font-semibold text-sm flex-1">
                {modulCount.learned}/{modulCount.total} modul dipelajari
              </div>
              <div
                className="text-sm font-semibold"
                style={{
                  width: `calc((50% / 1.5) + 6px)`,
                }}
              >
                Aksi Nyata
              </div>
            </div>
          </div>
          <div className="p-4 border-t-gray-20 border">
            <div className="flex justify-between">
              <div
                onClick={() => handleTrackerOpenTopic()}
                className="flex-auto"
              >
                <Link href={URL_TOPIC} passHref>
                  <Button color="white" fullWidth>
                    Buka Topik
                  </Button>
                </Link>
              </div>
              <div
                onClick={() => handleTrackerToActivity()}
                className="ml-2 flex-auto"
              >
                <Link href={URL_CONTINUE[status]} passHref>
                  <Button color="black" fullWidth>
                    {BUTTON_TEXT[status]}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      {!selectedProgress && (
        <InfoBox
          isError={isError}
          title={
            !isError
              ? 'Belum ada topik yang anda pelajari'
              : 'Terjadi masalah dalam memuat progres'
          }
          description={
            !isError
              ? 'Cobalah temukan di halaman daftar topik'
              : 'Silakan coba lagi dalam beberapa saat.'
          }
          isShowButton={!isError}
        />
      )}
    </div>
  )
}

export default TopicProgress
